import { useGetAvailableDataUntilApiV1 } from "@/api/__generated__/api.schema.ts";
import { useWorkspaceId } from "@/hooks/auth/use-workspace";
import { useIsMobile } from "@/hooks/use-mobile.tsx";
import { MONTHS } from "@/utils/time.ts";
import {
  BottomSheet,
  BottomSheetContent,
  BottomSheetHeader,
  BottomSheetTitle,
  BottomSheetTrigger,
  Button,
  DateRangePicker as DateRangePickerComp,
  Dropdown,
  DropdownContent,
  DropdownItem,
  DropdownTrigger,
  DropdownTriggerButton,
  Icon,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Separator,
  Typography,
} from "@backpac/monica";
import { type ComponentProps, useEffect, useMemo, useState } from "react";
import { Temporal } from "temporal-polyfill";

const recentDayFilters = [
  { label: "7 days", value: "7" },
  { label: "14 days", value: "14" },
  { label: "30 days", value: "30" },
] as const;

type recentDayFilterValues = (typeof recentDayFilters)[number]["value"];

const getDaysRange = (to: Temporal.PlainDate, days: number) => {
  const from = to.subtract({ days: days - 1 });
  return [from, to] as [Temporal.PlainDate, Temporal.PlainDate];
};

type DateRangeSelectProps = {
  onDateRangeSelected: (range: [Temporal.PlainDate, Temporal.PlainDate]) => void;
  selectedDateRange: [Temporal.PlainDate, Temporal.PlainDate] | undefined;
};

export const DateRangeSelect = ({ onDateRangeSelected, selectedDateRange }: DateRangeSelectProps) => {
  const workspaceId = useWorkspaceId();
  const [recentDays, setRecentDays] = useState<recentDayFilterValues | undefined>("7");
  const { data: availableDataUntil } = useGetAvailableDataUntilApiV1(workspaceId);

  const maxDate = useMemo(
    () => (availableDataUntil?.data.item ? Temporal.PlainDate.from(availableDataUntil.data.item) : undefined),
    [availableDataUntil],
  );

  const handleDateRangeSelected = (range: [Temporal.PlainDate, Temporal.PlainDate]) => {
    onDateRangeSelected(range);
    setRecentDays(undefined); // Clear recent days selection when custom range is selected
  };

  const handleRecentDaysSelected = (selected: recentDayFilterValues) => {
    setRecentDays(selected);
  };

  // Sync selected date range with recent days
  useEffect(() => {
    if (recentDays && maxDate) {
      onDateRangeSelected(getDaysRange(maxDate, Number(recentDays)));
    }
  }, [recentDays, maxDate, onDateRangeSelected]);

  return (
    <div className="flex items-center gap-[10px]">
      <DayRangeDropDown selected={recentDays} onSelect={handleRecentDaysSelected} />
      <DateRangePicker
        selectedDateRange={selectedDateRange}
        onDateRangeSelected={handleDateRangeSelected}
        maxDate={maxDate}
      />
    </div>
  );
};

type DayRangeDropDownProps = {
  selected?: recentDayFilterValues;
  onSelect: (selected: recentDayFilterValues) => void;
};

export const DayRangeDropDown = ({ selected, onSelect }: DayRangeDropDownProps) => {
  const selectedFilter = recentDayFilters.find((filter) => filter.value === selected);
  return (
    <Dropdown>
      <DropdownTrigger asChild>
        <DropdownTriggerButton className="font-semibold text-xs" size="md">
          {selectedFilter?.label ?? "Select"}
        </DropdownTriggerButton>
      </DropdownTrigger>
      <DropdownContent className="w-40" side="bottom" align="end">
        {recentDayFilters.map((filter) => (
          <DropdownItem key={filter.value} onSelect={() => onSelect(filter.value)}>
            <div className="flex flex-1 items-center justify-between gap-4px">
              <Typography size="sm" weight={600} color="textPrimary">
                {filter.label}
              </Typography>
              {selected === filter.value && <i className="i-bp-dropdown-check size-4 inline-block" />}
            </div>
          </DropdownItem>
        ))}
      </DropdownContent>
    </Dropdown>
  );
};

export const DateRangePicker = (props: ComponentProps<typeof DateRangePickerComp>) => {
  const isMobile = useIsMobile();
  if (isMobile) {
    return <DateRangePicker.BottomSheet {...props} />;
  }

  return <DateRangePicker.Popover {...props} />;
};

DateRangePicker.Popover = (props: ComponentProps<typeof DateRangePicker>) => {
  const [open, setOpen] = useState(false);

  // This is a workaround to close the popover when the selected date range is set
  useEffect(() => {
    if (props.selectedDateRange) {
      setOpen(false);
    }
  }, [props.selectedDateRange]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center justify-center hover:bg-backgroundLight rounded-full p-2 border border-1px bg-white"
        >
          <i className="inline-block i-bp-calendar size-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[390px] p-0" align="end">
        <DateRangePickerBody {...props} />
      </PopoverContent>
    </Popover>
  );
};

DateRangePicker.BottomSheet = (props: ComponentProps<typeof DateRangePicker>) => {
  const [open, setOpen] = useState(false);

  // This is a workaround to close the bottom sheet when the selected date range is set
  useEffect(() => {
    if (props.selectedDateRange) {
      setOpen(false);
    }
  }, [props.selectedDateRange]);

  return (
    <BottomSheet open={open} onOpenChange={setOpen}>
      <BottomSheetTrigger>
        <Icon name="calendar" bgColor="white" className="p-[10px] border border-1px" />
      </BottomSheetTrigger>
      <BottomSheetHeader className="sr-only">
        <BottomSheetTitle>Custom</BottomSheetTitle>
      </BottomSheetHeader>
      <BottomSheetContent>
        <DateRangePickerBody {...props} />
      </BottomSheetContent>
    </BottomSheet>
  );
};

// E.g., February 10 - 15 (6 days)
const formatDateRange = (from: Temporal.PlainDate, to: Temporal.PlainDate) => {
  const isSameMonth = from.monthCode === to.monthCode;

  const fromMonth = MONTHS[from.month - 1];
  const toMonth = MONTHS[to.month - 1];

  const fromText = `${fromMonth} ${from.day}`;
  const toText = isSameMonth ? `${to.day}` : `${toMonth} ${to.day}`;
  const dayCount = from.until(to).days + 1;

  return `${fromText} - ${toText} (${dayCount} days)`;
};

// NOTE: 120 days is the maximum allowed date range for the dashboard
const isDateRangeExceeding120Days = (from: Temporal.PlainDate, to: Temporal.PlainDate) => {
  const daysDifference = from.until(to).days + 1;
  return daysDifference > 120;
};

const DateRangePickerBody = ({
  selectedDateRange,
  onDateRangeSelected,
  maxDate,
}: ComponentProps<typeof DateRangePickerComp>) => {
  const [dateRange, setDateRange] = useState<[Temporal.PlainDate, Temporal.PlainDate] | undefined>(undefined);

  const applyDateRange = () => {
    if (dateRange) {
      // This is a workaround to trigger the date range selected event even when the date range is the same
      onDateRangeSelected([Temporal.PlainDate.from(dateRange[0]), Temporal.PlainDate.from(dateRange[1])]);
    }
  };

  useEffect(() => {
    if (selectedDateRange) {
      setDateRange(selectedDateRange);
    }
  }, [selectedDateRange]);

  if (dateRange == null) {
    return null;
  }

  const isExceeding120Days = isDateRangeExceeding120Days(dateRange[0], dateRange[1]);

  return (
    <div className="flex flex-col py-4">
      <Typography size="md" weight={600} color="textPrimary" className="text-center pb-4">
        Custom
      </Typography>
      <DateRangePickerComp selectedDateRange={dateRange} onDateRangeSelected={setDateRange} maxDate={maxDate} />
      <Separator />
      <Typography size="sm" weight={600} color="textPrimary" className="text-center py-4">
        {formatDateRange(dateRange[0], dateRange[1])}
      </Typography>
      <div className="px-4">
        <Button className="w-full" size="lg" onClick={applyDateRange} disabled={isExceeding120Days}>
          Apply
        </Button>
      </div>
    </div>
  );
};
